import {
  addAreaImgSheet,
  AreaImgSheetParameterRegularSheet,
  AreaImgSheetParameterTiledSheet,
} from "@/store/modes/import-sheet-mode-slice";
import { parseImageFromFile } from "@faro-lotv/flat-ui";
import { assert } from "@faro-lotv/foundation";
import { IElementType } from "@faro-lotv/ielement-types";
import {
  CommonUpdateProjectParams,
  UploadElementType,
} from "./use-upload-element-types";

export type UpdateAreaWithImgSheetTiledParams = {
  type: UploadElementType.areaImageTiled;

  /** The size of the tiles if the image is an image sheet tiled otherwise undefined */
  tilesSize: number;
} & Pick<
  AreaImgSheetParameterTiledSheet,
  "associatedRegularImageSheetInfo" | "selectedPdfPage"
>;

/**
 * Updates an area with a tiled image sheet parameter in importSheet slice.
 *
 * @param params - The parameters required to update the area, including tiled image sheet file.
 */
export async function updateAreaWithImgSheetTiled(
  params: CommonUpdateProjectParams & UpdateAreaWithImgSheetTiledParams,
): Promise<void> {
  assert(
    params.tilesSize,
    "The tiles size should be defined when trying to create an image sheet tiled.",
  );

  const imageWidth = params.tilesSize;
  const imageHeight = params.tilesSize;

  updateAreaWithImgSheetGeneric({
    elementType: IElementType.imgSheetTiled,
    imageWidth,
    imageHeight,
    ...params,
  });

  await Promise.resolve();
}

export type UpdateAreaWithImgSheetParams = {
  type: UploadElementType.areaImage;

  /** Indicates that the associated mutation should not be created when set to true. When not defined, it defaults to false. */
  doNotCreateAssociatedMutation?: boolean;
} & Pick<AreaImgSheetParameterRegularSheet, "selectedPdfPage">;

/**
 * Updates an area with an image sheet parameter in importSheet slice.
 *
 * @param params - The parameters required to update the area, including the image sheet file.
 * @returns A promise that resolves when the area is successfully updated.
 */
export async function updateAreaWithImgSheet(
  params: CommonUpdateProjectParams & UpdateAreaWithImgSheetParams,
): Promise<void> {
  if (!params.doNotCreateAssociatedMutation) {
    const { width: imageWidth, height: imageHeight } = await parseImageFromFile(
      params.file,
    );
    updateAreaWithImgSheetGeneric({
      elementType: IElementType.imgSheet,
      imageWidth,
      imageHeight,
      ...params,
    });
  }
}

type UpdateAreaWithImgSheetGenericProps = Pick<
  CommonUpdateProjectParams,
  "dispatch" | "file" | "additionalUrls" | "areaId"
> &
  (
    | Omit<
        AreaImgSheetParameterRegularSheet,
        "fileName" | "fileSize" | "additionalUrl" | "areaId"
      >
    | Omit<
        AreaImgSheetParameterTiledSheet,
        "fileName" | "fileSize" | "additionalUrl" | "areaId"
      >
  );

/**
 * Function that update importSheet slice
 */
function updateAreaWithImgSheetGeneric({
  dispatch,
  file,
  additionalUrls,
  areaId,
  ...otherProps
}: UpdateAreaWithImgSheetGenericProps): void {
  assert(areaId, "AreaId to update image sheet is not specified.");

  dispatch(
    addAreaImgSheet({
      areaId,
      fileName: file.name,
      fileSize: file.size,
      additionalUrl: additionalUrls[0],
      ...otherProps,
    }),
  );
}
